/**
 * FlowsSubtab — the "AI Flows" admin area. Lists the per-branch cell-selection
 * flows and opens the structured FlowEditor for edit / create. This is the
 * operator surface (Michael) for tuning the questions/constraints/weights the
 * assistant uses, and for adding a brand-new branch — all with no deploy.
 *
 * Built as a thin host so future AI flow types can slot in as additional lists.
 */
import { useEffect, useState } from 'preact/hooks'

import { getKnowledgeCapabilities } from '../../../services/ionKnowledgeService.js'
import { listFlows } from '../../../services/flowsService.js'
import { FlowEditor } from './FlowEditor.jsx'

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
}

export function FlowsSubtab() {
  // view: 'list' | { slug } (edit) | 'new'
  const [view, setView] = useState('list')
  const [flows, setFlows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [canPublish, setCanPublish] = useState(true)

  const refresh = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listFlows({ includeInactive: true })
      setFlows(data.flows || [])
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    getKnowledgeCapabilities()
      .then((c) => setCanPublish(c.can_publish_directly !== false))
      .catch(() => {})
  }, [])

  if (view === 'new' || (view && view.slug)) {
    return (
      <FlowEditor
        slug={view.slug || null}
        canPublish={canPublish}
        onSaved={refresh}
        onClose={() => {
          setView('list')
          refresh()
        }}
      />
    )
  }

  return (
    <div className="admin-aim-subtab-body">
      <div className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title">AI Flows</h2>
          <p className="admin-aim-sub">
            Per-branch cell-selection flows — the questions, hard constraints and
            scoring weights the assistant uses. Edits go live within ~1 minute, no deploy.
          </p>
        </div>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--send"
          onClick={() => setView('new')}
        >
          + New flow
        </button>
      </div>

      {error && <div className="admin-aim-error-banner">{error}</div>}

      {loading ? (
        <p className="admin-aim-empty">Loading flows…</p>
      ) : flows.length === 0 ? (
        <p className="admin-aim-empty">No selection flows yet — create one to get started.</p>
      ) : (
        <table className="admin-aim-table admin-flow-table">
          <thead>
            <tr>
              <th>Branch</th>
              <th>Slug</th>
              <th>Status</th>
              <th>Version</th>
              <th>Updated</th>
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {flows.map((f) => (
              <tr key={f.topic_id} className={f.enabled ? '' : 'admin-flow-row--off'}>
                <td>
                  <span className={`admin-flow-dot ${f.enabled ? 'is-on' : 'is-off'}`} aria-hidden="true" />
                  {f.spec?.branch_label || f.title}
                  {!f.parse_ok && (
                    <span className="admin-flow-badge admin-flow-badge--err" title={f.parse_error || ''}>
                      parse error
                    </span>
                  )}
                </td>
                <td>
                  <code>{f.slug}</code>
                </td>
                <td>{f.enabled ? 'Active' : 'Disabled'}</td>
                <td>{f.version}</td>
                <td>{formatDate(f.updated_at)}</td>
                <td>
                  <button
                    type="button"
                    className="admin-ai-btn admin-ai-btn--ghost"
                    onClick={() => setView({ slug: f.slug })}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
